import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function BudgetFormDialog({
  open,
  onOpenChange,
  initial,
  categories,
  existing,
  onSaved,
}) {
  const isEdit = Boolean(initial);
  const [category, setCategory] = useState(initial?.category || "");
  const [limit, setLimit] = useState(
    initial?.monthly_limit ? String(initial.monthly_limit) : ""
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory(initial?.category || "");
      setLimit(initial?.monthly_limit ? String(initial.monthly_limit) : "");
    }
  }, [open, initial]);

  const expenseCategories = (categories || []).filter((c) => c.type === "expense");
  const usedNames = new Set(
    (existing || [])
      .filter((b) => !initial || b.id !== initial.id)
      .map((b) => b.category)
  );
  const availableCategories = isEdit
    ? expenseCategories
    : expenseCategories.filter((c) => !usedNames.has(c.name));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(limit);
    if (!isEdit && !category) {
      toast.error("Selecciona una categoría");
      return;
    }
    if (!amt || amt <= 0) {
      toast.error("El límite debe ser mayor a 0");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/budgets/${initial.id}`, { monthly_limit: amt });
        toast.success("Presupuesto actualizado");
      } else {
        await api.post("/budgets", { category, monthly_limit: amt });
        toast.success("Presupuesto creado");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "No se pudo guardar el presupuesto"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131C2E] max-w-md" data-testid="budget-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#F1F5F9]">
            {isEdit ? "Editar presupuesto" : "Nuevo presupuesto"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            {isEdit
              ? "Ajusta el límite mensual de esta categoría."
              : "Elige una categoría de gasto y define cuánto puedes gastar al mes."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Categoría
            </Label>
            <Select
              value={category}
              onValueChange={setCategory}
              disabled={isEdit}
            >
              <SelectTrigger
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                data-testid="budget-category-select"
              >
                <SelectValue placeholder="Selecciona una categoría de gasto" />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E] max-h-64">
                {availableCategories.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-[#64748B]">
                    Todas tus categorías de gasto ya tienen presupuesto.
                  </div>
                ) : (
                  availableCategories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.name}
                        {c.group && (
                          <span className="text-xs text-[#94A3B8]">· {c.group}</span>
                        )}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Límite mensual (MXN)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="0.00"
              className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric text-lg"
              required
              data-testid="budget-limit-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#2A3447]"
              data-testid="budget-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
              data-testid="budget-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
